import { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MonitorIcon from '@mui/icons-material/Monitor';
import type { ScreenDetailed } from './MonitorManager';
import type { ScreenRole } from './types';

interface PopOutButtonProps {
  /** Available secondary screens (excludes the primary/current screen) */
  screens: ScreenDetailed[];
  /** Study UID to open in the child window */
  studyInstanceUID: string;
  /** Site ID for the study */
  siteId: string;
  /** Callback to open a viewer on a specific screen */
  onOpenOnScreen: (
    screen: ScreenDetailed,
    studyInstanceUID: string,
    siteId: string,
    role?: ScreenRole,
  ) => void;
}

/**
 * "Open on Monitor X" button for the ViewerTab title bar.
 *
 * Shows a dropdown of available secondary screens. Clicking a screen
 * opens the study in a standalone viewer window positioned on that screen.
 *
 * Hidden when there are no secondary screens available.
 */
export function PopOutButton({
  screens,
  studyInstanceUID,
  siteId,
  onOpenOnScreen,
}: PopOutButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  // Only show secondary (non-primary) screens
  const secondaryScreens = screens.filter((s) => !s.isPrimary);

  if (secondaryScreens.length === 0) return null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (screen: ScreenDetailed) => {
    onOpenOnScreen(screen, studyInstanceUID, siteId);
    handleClose();
  };

  return (
    <>
      <Tooltip title="Open on another monitor">
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{ width: 24, height: 24 }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {secondaryScreens.map((screen, idx) => (
          <MenuItem
            key={screen.label || idx}
            onClick={() => handleSelect(screen)}
            dense
          >
            <ListItemIcon>
              <MonitorIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={screen.label || `Monitor ${idx + 2}`}
              secondary={`${screen.width}×${screen.height}`}
              primaryTypographyProps={{ fontSize: 13 }}
              secondaryTypographyProps={{ fontSize: 11 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
